import { Button, notification } from "antd";
import React, { useEffect, useState } from "react";
import { httpService } from "../../../base/services/httpService.service";
import { SessionService } from "../../../base/services/session.service";
import { PlanInformation } from "./components/reviewPay/PlanInformation";
import { EnterpriseContact } from "./components/reviewPay/EnterpriseContact";
import PromoCodeField from "../../../components/Billing/PromoCodeField";
import { isEnterprisePacket } from "../../../components/Billing/enterprise";
import {
   EnterpriseRequestSource,
   notifyEnterpriseRequest
} from "../../../components/Billing/enterpriseRequest";
import { BillingReason } from "../../../components/Billing/billingCodes";
import { deriveOrganizationName } from "./registrationDefaults";
import { useNavigate } from "react-router-dom";
import {
   clearRegistration,
   getRegisteringAccount,
   getRegisteringPlan,
   isBilledYearly
} from "./registrationSession";
import { billingApi } from "../../../components/Billing/billingApi";
import { describeBillingError } from "../../../components/Billing/billingCodes";

// A detail of the account that was registered, shown only when there is one to
// show: the sign up form no longer asks for every field the review step can
// print, and a labelled blank reads as something that went missing.
const ReviewField = ({ label, value }) =>
   value ? (
      <div>
         <p>{label}</p>
         <span>{value}</span>
      </div>
   ) : null;

function RegisterStep3({ onBack, promoCode, onChangePromoCode }) {
   const nav = useNavigate();
   const [isLoading, setIsLoading] = useState(false);
   const [userInformation, setUserInformation] = useState();

   const [countries, setCountries] = useState([]);
   const [orgs, setOrgs] = useState([]);

   // A registration resumed from the login page arrives with the country as a
   // name, a fresh one as an id.
   const countryName = userInformation?.country?.name
      ? countries?.find((c) => c.name === userInformation?.country?.name)?.name
      : countries?.find((c) => c.id === userInformation?.country)?.name;

   const organizationTypeName = userInformation?.typeOrganization?.name
      ? orgs?.find((c) => c.name === userInformation?.typeOrganization?.name)?.name
      : orgs?.find((c) => c.id === userInformation?.typeOrganization)?.name;

   const hasOrganizationInformation = Boolean(
      userInformation?.organizationName || countryName || organizationTypeName
   );

   // Enterprise is asked for, not started: no trial, no promo code, and the way
   // to tell our team what the plan has to hold.
   const isEnterprise = isEnterprisePacket(getRegisteringPlan()?.id);
   const [enterpriseNeeds, setEnterpriseNeeds] = useState("");

   useEffect(() => {
      setUserInformation(getRegisteringAccount());
   }, []);

   useEffect(() => {
      httpService.get(`/Languages/getAllCountries`, (res) => {
         try {
            setCountries(res.data);
         } catch (error) {
            console.log(error);
         }
      });

      httpService.get(`/Account/getOrganizationTypes`, (res) => {
         try {
            setOrgs(res.data);
         } catch (error) {
            console.log(error);
         }
      });
   }, []);

   // The registration is done with: what is left of it in this tab goes, so
   // that a back button into the flow cannot subscribe the organization a
   // second time.
   const finishRegistration = (destination, state) => {
      clearRegistration();
      nav(destination, state ? { state } : undefined);
   };

   // The new billing flow. The trial starts through POST /Payment/subscriptions,
   // on the token confirming the email handed this sign up — no card, and the
   // subscription pauses rather than fails if none is added by the end. The call
   // is safe to repeat: a second one returns the same subscription, so there is
   // nothing to remember between attempts.
   //
   // Enterprise goes through the same call, which only records the request: the
   // organization waits for our team's terms (ENTERPRISE_OFFER_PENDING), and the
   // sign in shows that wait. A new organization can't have terms already, so
   // nothing is bought by it. Our team is then sent the request, with what the
   // review step was told the plan needs.
   const startTrial = async () => {
      const plan = getRegisteringPlan();
      const organizationId = SessionService.getItem("currentOrgId");

      setIsLoading(true);

      try {
         const billingStatus = await billingApi.createSubscription({
            organizationId,
            packetId: plan?.id,
            isMonthly: !isBilledYearly(),
            promoCode: isEnterprise ? "" : promoCode
         });

         const enterpriseMessageSent =
            billingStatus?.reason === BillingReason.ENTERPRISE_OFFER_PENDING
               ? await notifyEnterpriseRequest({
                    name: userInformation?.fullname,
                    email: userInformation?.email,
                    phone: userInformation?.phone,
                    organizationName:
                       userInformation?.organizationName ??
                       deriveOrganizationName(userInformation?.fullname),
                    organizationId,
                    source: EnterpriseRequestSource.SIGN_UP,
                    needs: enterpriseNeeds
                 })
               : undefined;

         finishRegistration("/auth/freeTrial/successful", {
            billingStatus,
            planName: plan?.name,
            trialDays: plan?.trialDays,
            enterpriseMessageSent
         });
      } catch (failure) {
         setIsLoading(false);
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      }
   };

   const handlePayment = () => {
      if (isLoading) return;
      startTrial();
   };

   return (
      <>
         <section className="review-pay-section">
            <div
               style={{
                  textAlign: "left",
                  marginBottom: "40px",
                  cursor: "pointer",
                  color: "#5770CB"
               }}
               onClick={onBack}
            >
               {" "}
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
               >
                  <path
                     d="M14.7358 8.2339L2.73584 8.2339M2.73584 8.2339L8.10255 2.86719M2.73584 8.2339L8.10255 13.6006"
                     stroke="#5770CB"
                     strokeLinecap="round"
                     strokeLinejoin="round"
                  />
               </svg>{" "}
               Go back to your package
            </div>
            <h1 style={{ margin: "40px", textAlign: "center" }} className="heading-select-plan">
               Review and confirm plan
            </h1>
            {userInformation && (
               <section className="create-information-review" style={{ alignContent: "center" }}>
                  <div className="left-block">
                     <h2 style={{ color: "#5770cb" }}>Your information</h2>
                     <ReviewField label="Full name" value={userInformation?.fullname} />
                     <ReviewField
                        label="Role within organization"
                        value={userInformation?.organizationRole}
                     />
                     <ReviewField label="Phone number" value={userInformation?.phone} />
                     <ReviewField label="Email" value={userInformation?.email} />
                  </div>
                  {/* A sign up no longer asks for any of this — the account is
                      opened on a name, an email and a phone number — so the
                      block stands down rather than heading three blanks. An
                      account resumed from the login page does arrive with its
                      organization, and keeps it. */}
                  {hasOrganizationInformation && (
                     <div className="right-block">
                        <h2 style={{ color: "#5770cb" }}>Organization’s information</h2>
                        <ReviewField label="Name" value={userInformation?.organizationName} />
                        <ReviewField label="Country" value={countryName} />
                        <ReviewField label="Type of organization" value={organizationTypeName} />
                     </div>
                  )}
               </section>
            )}
            <PlanInformation />
            {isEnterprise && <EnterpriseContact needs={enterpriseNeeds} onChange={setEnterpriseNeeds} />}
            <div className="payment-prefer"></div>
         </section>
         {/* Asked for here, beside the price it changes and the button that
             sends it with the subscription. The account exists by this step,
             which checking a code needs: it is a billing call, made with the
             token confirming the email gave this sign up. */}
         {!isEnterprise && (
            <PromoCodeField
               promoCode={promoCode}
               onChange={onChangePromoCode}
               organizationId={SessionService.getItem("currentOrgId")}
               packetId={getRegisteringPlan()?.id}
               isMonthly={!isBilledYearly()}
            />
         )}
         <div
            className="register-button-group"
            style={{ marginTop: "5%", width: "100%", display: "flex", justifyContent: "center" }}
         >
            <Button
               onClick={handlePayment}
               className="kapSurveyPrimaryButton"
               style={{
                  border: "none",
                  color: "white",
                  fontSize: "600",
                  textAlign: "center",
                  justifyContent: "center"
               }}
               disabled={isLoading}
               loading={isLoading}
            >
               {isEnterprise ? "Request Enterprise" : "Start your free trial"}
            </Button>
         </div>
      </>
   );
}

export default RegisterStep3;
